
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { loginRequest, verifyOtpRequest, sendOtpRequest } from "../../store/features/user/userActions";
import { resetLoginState } from "../../store/features/user/userSlice";
import { FiArrowLeft, FiX } from "react-icons/fi";
import LoginCredentialsForm from "./LoginCredentialsForm";
import LoginOtpForm from "./LoginOtpForm";

interface LoginProps {
  isModal?: boolean;
  onSwitch?: () => void;
  onClose?: () => void;
}

const Login: React.FC<LoginProps> = ({
  isModal = false,
  onSwitch,
  onClose,
}) => {
  const [mobileNumber, setMobileNumber] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { isAuthenticated, loading, loginStep, otpChallengeId, loginError, canResendAt, resendCount } = useSelector(
    (state: RootState) => state.user,
  );

  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (!canResendAt) {
        setTimeLeft(0);
        return;
    }
    
    const calculateTimeLeft = () => {
        const expiryTime = new Date(canResendAt).getTime();
        const now = new Date().getTime();
        const diff = Math.ceil((expiryTime - now) / 1000);
        return diff > 0 ? diff : 0;
    };

    setTimeLeft(calculateTimeLeft());

    const interval = setInterval(() => {
        const diff = calculateTimeLeft();
        setTimeLeft(diff);
        if (diff <= 0) {
            clearInterval(interval);
        }
    }, 1000);

    return () => clearInterval(interval);
  }, [canResendAt]);

  const handleResendOtp = () => {
      if(otpChallengeId) {
          dispatch(sendOtpRequest({ otpChallengeId }));
      }
  };

  const from = (location.state as any)?.from || "/";

  // Reset state on unmount or when opening
  useEffect(() => {
    // Mount
    dispatch(resetLoginState());
    setMobileNumber("");
    setPassword("");
    setOtp("");
    setError("");

    return () => {
      // Unmount
      dispatch(resetLoginState());
      setMobileNumber("");
      setPassword("");
      setOtp("");
      setError("");
    };
  }, [dispatch]);

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!mobileNumber) {
      setError("Mobile Number is required");
      return;
    }
    // if (!password) {
    //   setError("Password is required");
    //   return;
    // }
    dispatch(loginRequest({ mobileNumber }));
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!otp) {
        setError("OTP is required");
        return;
    }
    if (!otpChallengeId) {
        setError("Session expired. Please login again.");
        return;
    }
    dispatch(verifyOtpRequest({ otpChallengeId, otp }));
  };

  useEffect(() => {
    if (isAuthenticated) {
      if (isModal && onClose) {
        onClose();
      } else {
        navigate(from, { replace: true });
      }
    }
  }, [isAuthenticated, navigate, from, isModal, onClose]);

  // Handle errors from store - but synchronize strictly
  useEffect(() => {
    if (loginError) {
      setError(loginError);
    } else {
      setError(""); // Clear local error if store error is cleared
    }
  }, [loginError]);

  // Prevent flash of login form during redirection
  if (isAuthenticated && !isModal) {
      return null; 
  }

  return (
    <div className={`relative h-screen w-full flex overflow-hidden bg-slate-50 font-sans`}>
      
      {/* LEFT SIDE - Hero/Art (Desktop Only) */}
      {!isModal && (
        <div className="hidden lg:flex lg:w-1/2 relative bg-indigo-600 overflow-hidden items-center justify-center">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-20">
               <div className="absolute top-0 -left-1/4 w-full h-full bg-gradient-to-br from-purple-400 to-indigo-500 rounded-full blur-3xl mix-blend-multiply filter opacity-70 animate-blob"></div>
               <div className="absolute -bottom-1/2 left-1/4 w-full h-full bg-gradient-to-tr from-indigo-400 to-emerald-400 rounded-full blur-3xl mix-blend-multiply filter opacity-70 animate-blob animation-delay-2000"></div>
               <div className="absolute top-1/2 left-1/2 w-full h-full bg-gradient-to-bl from-pink-300 to-rose-300 rounded-full blur-3xl mix-blend-multiply filter opacity-70 animate-blob animation-delay-4000"></div>
               <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
                   <filter id="noiseFilter">
                       <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch"/>
                   </filter>
                   <rect width="100%" height="100%" filter="url(#noiseFilter)" opacity="0.1"/>
               </svg>
            </div>
            
            {/* Content Overlay */}
            <div className="relative z-10 p-12 text-center text-white space-y-8 max-w-lg">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-lg rounded-2xl mx-auto flex items-center justify-center mb-6 shadow-2xl border border-white/10">
                    <span className="text-3xl font-bold">A</span>
                </div>
                <h2 className="text-4xl font-extrabold tracking-tight leading-tight">
                    The Future of  <br/> 
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-200 to-pink-200">Digital Payments</span>
                </h2>
                <p className="text-indigo-100 text-lg leading-relaxed font-light opacity-90">
                    Experience seamless transactions, robust security, and real-time analytics all in one unified platform.
                </p>

                 {/* Floating Cards Graphic */}
                 <div className="relative mt-12 h-64 w-full perspective-1000">
                      <div className="absolute top-0 left-10 w-48 h-32 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl transform -rotate-12 translate-y-4 hover:rotate-0 transition-transform duration-500"></div>
                      <div className="absolute top-4 right-12 w-48 h-32 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl transform rotate-6 translate-y-0 hover:rotate-0 transition-transform duration-500 z-10 flex items-center justify-center">
                          <div className="h-2 w-24 bg-white/20 rounded-full"></div>
                      </div>
                 </div>
            </div>
        </div>
      )}

      {/* RIGHT SIDE - Form */}
      <div className={`w-full ${!isModal ? 'lg:w-1/2' : ''} h-full overflow-y-auto custom-scrollbar flex flex-col p-4 sm:p-6 pb-[5px] relative`}>
        {/* Soft Background for Mobile/Right Side */}
        <div className="absolute inset-0 -z-10 bg-white">
             <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-indigo-50/50 blur-3xl"></div>
             <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full bg-purple-50/50 blur-3xl"></div>
        </div>

        {!isModal && (
          <RouterLink
            to="/"
            className="absolute top-6 left-6 lg:top-12 lg:left-12 flex items-center gap-2 text-gray-400 hover:text-violet-600 transition-colors font-medium text-sm group"
          >
            <span className="p-2 rounded-full bg-gray-50 group-hover:bg-violet-50 transition-colors">
                <FiArrowLeft size={16} />
            </span>
            <span className="hidden sm:inline">Back to Home</span>
          </RouterLink>
        )}

        {isModal && onClose && (
          <button
            onClick={() => {
                dispatch(resetLoginState());
                onClose();
            }}
            className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100/50 rounded-full transition-all z-50"
          >
            <FiX size={24} />
          </button>
        )}

        {/* Content Container */}
        <div className="w-full max-w-md mx-auto space-y-4 my-auto">
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
              {loginStep === 'otp' ? 'Verification' : 'Welcome back'}
            </h1>
            <p className="text-gray-400 text-sm">
              {loginStep === 'otp' ? 'We sent a 6-digit code to your device.' : 'Please enter your details to sign in.'}
            </p>
          </div>

          {loginStep === 'credentials' ? (
            <LoginCredentialsForm
                mobileNumber={mobileNumber}
                setMobileNumber={setMobileNumber}
                password={password}
                setPassword={setPassword}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                onSubmit={handleCredentialsSubmit}
                loading={loading}
                error={error}
            />
          ) : (
            <LoginOtpForm
                mobileNumber={mobileNumber}
                otp={otp}
                setOtp={setOtp}
                onSubmit={handleOtpSubmit}
                loading={loading}
                error={error}
                timeLeft={timeLeft}
                resendCount={resendCount}
                onResend={handleResendOtp}
                onBack={() => dispatch(resetLoginState())}
            />
          )}

          <div className="pt-6 text-center">
              <p className="text-sm text-gray-500">
                {`New to CashQuin?`}
                {isModal ? (
                <button
                    onClick={onSwitch}
                    className="ml-1 font-semibold text-violet-600 hover:text-violet-500 transition-colors hover:underline"
                >
                    Create Account
                </button>
                ) : (
                <RouterLink
                    to="/register"
                    className="ml-1 font-semibold text-violet-600 hover:text-violet-500 transition-colors hover:underline"
                >
                    Create Account
                </RouterLink>
                )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
